import IBaseListingResponse from "../../../utils/api/base-listing-response";
import {
  IEnumResource,
  IInventoryShortage,
  IInventoryShortageCoverage,
  IInventoryShortageOrder,
  IInventoryShortageProduct,
  IInventoryShortageProductOrder,
  IInventoryShortageReceiptCorrectionBatch,
  IInventoryShortageSourceVerification,
  IInventoryShortageStore,
  IUserLight,
} from "./response";

const unwrapApiResource = (response: any) =>
  response?.success !== undefined && response?.data !== undefined
    ? response.data
    : response;

/** Quantities are integer grams; a missing one is 0, never NaN. */
const toGrams = (value: any): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const mapEnumResource = (item: any): IEnumResource | null => {
  if (!item) return null;
  return {
    value: item.value,
    name: item.name ?? null,
  };
};

const mapUserLight = (item: any): IUserLight | null => {
  if (!item) return null;
  return {
    id: item.id,
    firstName: item.first_name ?? item.firstName ?? null,
    lastName: item.last_name ?? item.lastName ?? null,
    fullName: item.full_name ?? item.fullName ?? null,
  };
};

const mapOrder = (item: any): IInventoryShortageOrder | null => {
  if (!item) return null;
  return {
    id: item.id,
    number: item.number ?? null,
    status: mapEnumResource(item.status),
  };
};

const mapProductOrder = (item: any): IInventoryShortageProductOrder | null => {
  if (!item) return null;
  return {
    id: item.id,
    actualQuantityPhysical:
      item.actual_quantity_physical ?? item.actualQuantityPhysical ?? null,
    reservedCount: item.reserved_count ?? item.reservedCount ?? null,
    isAccepted: item.is_accepted ?? item.isAccepted ?? null,
  };
};

const mapProduct = (item: any): IInventoryShortageProduct | null => {
  if (!item) return null;
  return {
    id: item.id,
    name: item.name ?? null,
    barcode: item.barcode ?? null,
  };
};

const mapStore = (item: any): IInventoryShortageStore | null => {
  if (!item) return null;
  return {
    id: item.id,
    name: item.name ?? null,
  };
};

const mapSourceVerification = (
  item: any,
): IInventoryShortageSourceVerification | null => {
  if (!item) return null;
  return {
    id: item.id,
    method: mapEnumResource(item.method),
    scannedBarcode: item.scanned_barcode ?? item.scannedBarcode ?? null,
    measuredQuantityPhysical:
      item.measured_quantity_physical ?? item.measuredQuantityPhysical ?? null,
  };
};

const mapReceiptCorrectionBatch = (
  item: any,
): IInventoryShortageReceiptCorrectionBatch => ({
  batchId: item.batch_id ?? item.batchId ?? item.id,
  batchNumber: item.batch_number ?? item.batchNumber ?? null,
  expirationDate: item.expiration_date ?? item.expirationDate ?? null,
  purchaseDate: item.purchase_date ?? item.purchaseDate,
  availableQuantityPhysical: toGrams(
    item.available_quantity_physical ?? item.availableQuantityPhysical,
  ),
  allocatedQuantityPhysical: toGrams(
    item.allocated_quantity_physical ?? item.allocatedQuantityPhysical,
  ),
});

const mapCoverage = (item: any): IInventoryShortageCoverage => ({
  id: item.id ?? null,
  type: mapEnumResource(item.type),
  batchId: item.batch_id ?? item.batchId ?? null,
  batchNumber:
    item.batch_number ??
    item.batchNumber ??
    item.batch?.batch_number ??
    item.batch?.batchNumber ??
    null,
  allocationId: item.allocation_id ?? item.allocationId ?? null,
  quantityPhysical: toGrams(item.quantity_physical ?? item.quantityPhysical),
  operationToken: item.operation_token ?? item.operationToken ?? null,
  notes: item.notes ?? null,
  createdAt: item.created_at ?? item.createdAt ?? null,
  createdBy: mapUserLight(item.created_by ?? item.createdBy),
});

const mapArray = <T>(value: any, map: (item: any) => T): T[] =>
  Array.isArray(value) ? value.map(map) : [];

export const mapInventoryShortage = (item: any): IInventoryShortage => ({
  id: item.id,
  status: mapEnumResource(item.status),
  orderId: item.order_id ?? item.orderId ?? null,
  order: mapOrder(item.order),
  productOrderId: item.product_order_id ?? item.productOrderId ?? null,
  productOrder: mapProductOrder(item.product_order ?? item.productOrder),
  productId: item.product_id ?? item.productId ?? null,
  product: mapProduct(item.product),
  storeId: item.store_id ?? item.storeId ?? null,
  store: mapStore(item.store),
  sourceVerificationId:
    item.source_verification_id ?? item.sourceVerificationId ?? null,
  sourceVerification: mapSourceVerification(
    item.source_verification ?? item.sourceVerification,
  ),
  initialQuantityPhysical: toGrams(
    item.initial_quantity_physical ?? item.initialQuantityPhysical,
  ),
  remainingQuantityPhysical: toGrams(
    item.remaining_quantity_physical ?? item.remainingQuantityPhysical,
  ),
  coveredQuantityPhysical: toGrams(
    item.covered_quantity_physical ?? item.coveredQuantityPhysical,
  ),
  cancelledQuantityPhysical: toGrams(
    item.cancelled_quantity_physical ?? item.cancelledQuantityPhysical,
  ),
  varianceQuantityPhysical: toGrams(
    item.variance_quantity_physical ?? item.varianceQuantityPhysical,
  ),
  correctedQuantityPhysical: toGrams(
    item.corrected_quantity_physical ?? item.correctedQuantityPhysical,
  ),
  factorAccountingSystem:
    item.factor_accounting_system ?? item.factorAccountingSystem ?? null,
  // Absent, `null` and `[]` all mean "the server imposes no source restriction".
  receiptCorrectionBatches: mapArray(
    item.receipt_correction_batches ?? item.receiptCorrectionBatches,
    mapReceiptCorrectionBatch,
  ),
  coverages: mapArray(item.coverages, mapCoverage),
  canCover: item.can_cover ?? item.canCover ?? false,
  canCorrectReceipt:
    item.can_correct_receipt ?? item.canCorrectReceipt ?? false,
  canSettle: item.can_settle ?? item.canSettle ?? false,
  resolutionUnavailableReason:
    item.resolution_unavailable_reason ??
    item.resolutionUnavailableReason ??
    null,
  settlement: item.settlement ?? null,
  notes: item.notes ?? null,
  createdAt: item.created_at ?? item.createdAt ?? null,
  createdBy: mapUserLight(item.created_by ?? item.createdBy),
  updatedAt: item.updated_at ?? item.updatedAt ?? null,
});

/**
 * `cover` and `receipt-correction` return the same resource as `GET /{id}`, so
 * the row can be replaced from the response instead of re-reading the list.
 */
export const mapInventoryShortageResource = (
  response: any,
): IInventoryShortage => mapInventoryShortage(unwrapApiResource(response));

/**
 * The endpoint returns `{data, page, perPage, lastPage, total}` while the shared
 * listing interface expects `currentPage`, so the page number is translated here
 * rather than by widening an interface 40+ services depend on.
 */
export const normalizeInventoryShortageListingResponse = (
  response: any,
): IBaseListingResponse<IInventoryShortage> => {
  const payload = unwrapApiResource(response);
  const rows = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];

  return {
    data: rows.map(mapInventoryShortage),
    total: payload?.total ?? payload?.meta?.total ?? rows.length,
    currentPage: payload?.page ?? payload?.currentPage ?? 1,
    lastPage: payload?.lastPage ?? payload?.last_page ?? 1,
  };
};
